import React, { PureComponent, ReactNode, useEffect, useState } from 'react';
import { Button, Checkbox, DatePicker, Input, message, notification, Table, Tooltip } from 'antd';
import moment, { Moment } from 'moment';
import Hotkeys from 'react-hot-keys';
import RightPopUp from '../components/common/RightPopUp';
import { batch, connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../store/reducers';
import { Actions } from '../store/actions';
import { setLabVitals, updatePrintConfig } from '../store/actions/prescriptionPad';
import {
    Interpretation,
    LabVitalEntity,
    PatientPersonalProfile,
    Unit,
} from '../store/types/prescription';
import labVitalInterpretation from '../api/labVitalInterpretation';
import {
    getLabVitals,
    getPatientPersonalProfile,
    getPrintConfigs,
} from '../store/selectors/prescriptionPad';
import { generateDySecId, getAgeInMonths } from '../store/utils';
import { INTERPOLATION_VALUES } from '../constants';
import {
    Data,
    DataType,
    LabVitalType,
    TableDataType,
    TransformedDataType,
    right,
} from '../store/types/LabTests';
import { AnimatePresence } from 'framer-motion';
import {
    AllKeysNameType,
    buildDateWiseLabVitalArray,
    calculateOutOfRange,
    calculateTotalFilledElements,
    calculateTotalFilledElementsInOneLabTests,
    filterAllFilledTableData,
    filterTableDataBasedOnFuseSearchSet,
    getAllLabTestsVitalKeys,
    getAllLabTestsVitalKeysAndName,
    getAllLabVitalKeysNotPresentInLabTest,
    tranformData,
    transformAccordingToTableDataSource,
    updateElementAndGetTableObj,
} from '../components/prescriptionPad/rightPanel/workspace/labVitalsUtils';
import { PrintConfig, PrintConfigItemV2 } from '../api/pushVisit';
import debounce from 'lodash/debounce';
import { captureError } from './captureError';
import { track } from '../tracking';
import { LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION } from '../labTestsConstants';
import Fuse from 'fuse.js';

const defaultScrollValues = { x: '100%', y: '100%' };

export function structureLabVital(option: LabVitalEntity): LabVitalEntity {
    return {
        id: option.id,
        name: option.name,
        unit_dislay_name: option.unit_dislay_name,
        value: option.value,
        interpretation: option?.interpretation,
        date: moment(option.date).toISOString() || new Date().toISOString(),
        remark: option.remark,
        unit: option.unit || option?.all_units?.[0],
        all_units: option.all_units,
    };
}

type LabTestsTableState = {
    expandedRowKeys: number[];
    searchResult: Set<string> | undefined;
    activeDatePickerId?: number;
};

type LabTestsTableProps = {
    data: Data[];
    patient: PatientPersonalProfile | undefined;
    labVitals: LabVitalEntity[] | undefined;
    labDataNotPresentInTable: LabVitalEntity[];
    tableData: TableDataType[];
    dateColumns: (string | undefined)[];
    setDateColumns: (dateColumns: (string | undefined)[]) => void;
    onSaveElementInLocalState: (labVitalObj: Partial<LabVitalType>, i: number) => void;
    setTableData: (tableData: TableDataType[]) => void;
};

const fuseConfig = {
    keys: ['name', 'unit_dislay_name'],
    threshold: 0.4,
    ignoreLocation: true,
};
class LabTestsTable extends PureComponent<LabTestsTableProps, LabTestsTableState> {
    fuse = new Fuse([] as AllKeysNameType[], fuseConfig);
    constructor(props: LabTestsTableProps) {
        super(props);
        this.state = {
            expandedRowKeys: [],
            searchResult: undefined,
        };
        this.fuse = new Fuse(getAllLabTestsVitalKeysAndName(this.props.data) || [], fuseConfig);
    }

    componentDidMount = async () => {
        this.fuse = new Fuse(getAllLabTestsVitalKeysAndName(this.props.data) || [], fuseConfig);
    };

    disabledFutureDate = (current: Moment): boolean => {
        return moment(current, 'YYYY-MM-DD') > moment(moment(), 'YYYY-MM-DD');
    };

    onChangeDate = (value: moment.Moment | null, i: number) => {
        const { dateColumns, setDateColumns } = this.props;

        setDateColumns(
            dateColumns?.map((date, index: number) => {
                if (i === index) {
                    return value?.toISOString();
                }
                return date;
            }),
        );
        this.setState({
            activeDatePickerId: undefined,
        });
    };

    buildColumns = (columnsCount: number) => {
        const { activeDatePickerId } = this.state;
        const {
            data,
            tableData,
            dateColumns,
            setDateColumns,
            onSaveElementInLocalState,
            setTableData,
        } = this.props;
        const dateColumn = [];

        for (let i = 0; i < columnsCount; i++) {
            const dateId = `date-id-${i}`;
            dateColumn.push({
                key: `date-${i}`,
                title: () => {
                    return (
                        <DatePicker
                            id={dateId}
                            open={i === activeDatePickerId ? true : false}
                            picker={'date'}
                            allowClear={false}
                            className="bg-darwin-neutral-50 rounded-8 border-none py-9 px-12 mx-8 antd-date"
                            suffixIcon={
                                <div className="ml-40">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 14 16"
                                        className="w-14 w-16"
                                    >
                                        <path
                                            d="M4.75 2H9.25V0.75C9.25 0.34375 9.5625 0 10 0C10.4062 0 10.75 0.34375 10.75 0.75V2H12C13.0938 2 14 2.90625 14 4V14C14 15.125 13.0938 16 12 16H2C0.875 16 0 15.125 0 14V4C0 2.90625 0.875 2 2 2H3.25V0.75C3.25 0.34375 3.5625 0 4 0C4.40625 0 4.75 0.34375 4.75 0.75V2ZM1.5 14C1.5 14.2812 1.71875 14.5 2 14.5H12C12.25 14.5 12.5 14.2812 12.5 14V6H1.5V14Z"
                                            fill="#616A8F"
                                        />
                                    </svg>
                                </div>
                            }
                            onChange={(value) => {
                                this.onChangeDate(value, i);
                            }}
                            onClick={() => {
                                this.setState({ activeDatePickerId: i });
                            }}
                            onBlur={() => {
                                this.setState({ activeDatePickerId: undefined });
                            }}
                            value={dateColumns[i] ? moment(dateColumns[i]) : undefined}
                            disabledDate={this.disabledFutureDate}
                            format="DD MMM'YY"
                        />
                    );
                },
                width: 200,
                render: (ele: TransformedDataType, __: unknown, index: number): ReactNode => {
                    const { patient } = this.props;
                    if (ele?.type == 'parent-table') {
                        return <p className="text-14 text-darwin-neutral-900"></p>;
                    }
                    const element = ele[i];
                    const isDisabled = !ele?.disabledColumn?.[i];

                    return (
                        <div
                            onClick={() => {
                                if (isDisabled) {
                                    this.setState({ activeDatePickerId: i });
                                    document.getElementById(dateId)?.focus();
                                }
                            }}
                        >
                            <LabResultsInput
                                element={element}
                                disabled={isDisabled}
                                patient={patient}
                                onSave={(labVitalObj) => onSaveElementInLocalState(labVitalObj, i)}
                            />
                        </div>
                    );
                },
            });
        }

        return [
            {
                fixed: true,
                key: 'search',
                width: 370,

                title: () => {
                    return (
                        <div className="pt-16 pb-8">
                            <div className="max-w-264 px-12 w-full rounded-8 border border-darwin-neutral-200 flex items-center space-x-10 focus-within:bg-neutral-9 bg-white">
                                <div className="flex items-center space-x-12">
                                    <svg
                                        className="w-16 h-16 fill-darwin-neutral-400"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                    >
                                        <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    className="w-full text-14 placeholder-darwin-neutral-400 text-neutral-black bg-transparent py-9"
                                    placeholder="Search test name..."
                                    onChange={(e) => {
                                        this.handleSearch(e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    );
                },
                render: (ele: TransformedDataType): ReactNode => {
                    if ((ele as DataType).type == 'parent-table') {
                        return (
                            <p className="pl-8 py-2 text-14 text-darwin-neutral-900 font-700">
                                {(ele as DataType).name}(
                                {calculateTotalFilledElementsInOneLabTests(
                                    tableData,
                                    dateColumns,
                                    (ele as DataType).id,
                                )}
                                )
                            </p>
                        );
                    }

                    const element = ele[0];
                    return (
                        <p className="pl-8 text-14 text-darwin-neutral-900">
                            {element?.unit_dislay_name}
                        </p>
                    );
                },
            },
            ...dateColumn,
            {
                dataIndex: 'expand',
                key: 'expand',
                fixed: 'right' as right,
                title: () => {
                    return (
                        <div className="flex items-center justify-center w-full h-full">
                            <div
                                className="p-12 rounded-8 border border-darwin-primary-500"
                                style={{
                                    width: 'fit-content',
                                }}
                                onClick={() => {
                                    batch(() => {
                                        setDateColumns([...dateColumns, undefined]);
                                        setTableData(tranformData(tableData, data, true));
                                    });
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 14 14"
                                    className="w-14 h-14"
                                >
                                    <path
                                        d="M13.5 7C13.5 7.4375 13.1562 7.75 12.75 7.75H7.75V12.75C7.75 13.1875 7.40625 13.5312 7 13.5312C6.5625 13.5312 6.25 13.1875 6.25 12.75V7.75H1.25C0.8125 7.75 0.5 7.4375 0.5 7.03125C0.5 6.59375 0.8125 6.25 1.25 6.25H6.25V1.25C6.25 0.84375 6.5625 0.53125 7 0.53125C7.40625 0.53125 7.75 0.84375 7.75 1.25V6.25H12.75C13.1562 6.25 13.5 6.59375 13.5 7Z"
                                        fill="#004BA8"
                                    />
                                </svg>
                            </div>
                        </div>
                    );
                },
                width: 50,
                render: () => null,
            },
        ];
    };

    handleRowExpand = (key: number) => {
        const { expandedRowKeys } = this.state;
        const isKeyPresent =
            expandedRowKeys.findIndex((expandedRowKey) => expandedRowKey === key) < 0
                ? false
                : true;

        if (isKeyPresent) {
            const keys = expandedRowKeys.filter((expandedRowKey) => expandedRowKey !== key);
            this.setState({ expandedRowKeys: keys });
        } else {
            const keys = [...expandedRowKeys, key];
            this.setState({ expandedRowKeys: keys });
        }
    };

    expandIcon = ({
        expanded,
        onExpand,
        record,
    }: {
        expanded: boolean;
        onExpand: any;
        record: any;
    }) => {
        if (record.children) {
            return (
                <div className="flex items-center justify-center">
                    {expanded ? (
                        <svg
                            onClick={() => onExpand(record, true)}
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="2"
                            viewBox="0 0 12 2"
                            fill="none"
                        >
                            <path
                                d="M11.6875 0.75C11.6875 1.13281 11.3867 1.40625 11.0312 1.40625H0.96875C0.585938 1.40625 0.3125 1.13281 0.3125 0.777344C0.3125 0.394531 0.585938 0.09375 0.96875 0.09375H11.0312C11.3867 0.09375 11.6875 0.394531 11.6875 0.75Z"
                                fill="#616A8F"
                            />
                        </svg>
                    ) : (
                        <svg
                            onClick={() => onExpand(record, false)}
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                        >
                            <path
                                d="M11.6875 5.75C11.6875 6.13281 11.3867 6.40625 11.0312 6.40625H6.65625V10.7812C6.65625 11.1641 6.35547 11.4648 6 11.4648C5.61719 11.4648 5.34375 11.1641 5.34375 10.7812V6.40625H0.96875C0.585938 6.40625 0.3125 6.13281 0.3125 5.77734C0.3125 5.39453 0.585938 5.09375 0.96875 5.09375H5.34375V0.71875C5.34375 0.363281 5.61719 0.0898438 6 0.0898438C6.35547 0.0898438 6.65625 0.363281 6.65625 0.71875V5.09375H11.0312C11.3867 5.09375 11.6875 5.39453 11.6875 5.75Z"
                                fill="#616A8F"
                            />
                        </svg>
                    )}
                </div>
            );
        }
        return null;
    };

    handleSearch = (input: string) => {
        const { tableData } = this.props;
        const searchResult = input
            ? new Set(
                  this.fuse
                      .search(input)
                      .map((t) => t.item)
                      ?.map((res) => res.id),
              )
            : undefined;
        this.setState({
            searchResult: searchResult,
            expandedRowKeys: tableData.map((_: any, i: number) => i),
        });
    };

    render(): ReactNode {
        const { expandedRowKeys, searchResult } = this.state;
        const { tableData, dateColumns } = this.props;

        const columns = this.buildColumns(dateColumns?.length);
        const filteredData = filterTableDataBasedOnFuseSearchSet(searchResult, tableData);
        const data = transformAccordingToTableDataSource(filteredData, dateColumns);

        return (
            <Table
                className="overflow-auto h-full w-full hide-scrollbar lab-vital-template-table-row"
                showHeader={true}
                sticky
                onRow={(record) => ({
                    onClick: () => {
                        if (record.type === 'parent-table') {
                            this.handleRowExpand(record.key);
                        }
                    },
                })}
                pagination={false}
                columns={columns}
                scroll={defaultScrollValues}
                dataSource={data as DataType[]}
                expandable={{
                    expandedRowKeys,
                    expandIconColumnIndex: columns.length - 1,
                    expandIcon: this.expandIcon,
                }}
            />
        );
    }
}

export default LabTestsTable;

type LabResultsInputProps = {
    element: LabVitalType;
    onSave: (labVitalObj: Partial<LabVitalType>) => void;
    disabled?: boolean;
    patient?: PatientPersonalProfile | undefined;
};

type LabResultsInputState = {
    input?: string;
    unit?: Unit;
};
class LabResultsInput extends PureComponent<LabResultsInputProps, LabResultsInputState> {
    state: LabResultsInputState = {
        input: this.props.element?.value,
    };

    componentDidUpdate = (prevProps: LabResultsInputProps) => {
        const { element } = this.props;
        if (element?.value !== prevProps.element?.value) {
            this.setState({
                input: element?.value,
            });
        }
    };

    fetchLabVitalInterpretation = async (input: string, unit?: Unit) => {
        const { element, patient } = this.props;

        if (!element?.id || !input || !patient?.gender || !patient?.age) {
            return;
        }

        try {
            const data = await labVitalInterpretation({
                request_id: generateDySecId(),
                normalisation: true,
                request_body: [
                    {
                        eka_id: element?.id,
                        value: Number(input),
                        value_unit: unit?.name,
                        value_unit_eka_id: unit?.id,
                        gender: patient?.gender,
                        age: getAgeInMonths(patient.age),
                        age_unit: 'months',
                    },
                ],
            });

            return data;
        } catch (error) {
            console.error(error);
            captureError(error as Error);
            track('ERROR', {
                on: 'labTests => fetchLabVitalInterpretation',
                what: 'failed to fetch lab results interaction',
            });
        }

        return null;
    };

    onClickSave = async (input: string, unit?: Unit) => {
        const { element, onSave } = this.props;

        onSave({
            id: element.id,
            value: input,
            unit: unit,
        });

        if (input.trim()) {
            const data = await this.fetchLabVitalInterpretation(input, unit);

            onSave({
                id: element.id,
                interpretation: INTERPOLATION_VALUES.find(
                    (interpolationValue) =>
                        interpolationValue.id === data?.response?.[0]?.interpretation_eka_id,
                ) as Interpretation,
            });
        } else {
            onSave({
                id: element.id,
                interpretation: undefined,
            });
        }
    };

    saveInput = debounce(this.onClickSave, 300);

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { element } = this.props;
        const value = e.target.value;
        this.setState({ input: value });
        this.saveInput(value, element?.unit);
    };

    render(): React.ReactNode {
        const { input } = this.state;
        const { disabled, element } = this.props;

        const isHighLowAbnormalInterpretation =
            LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION[
                element.interpretation
                    ?.eka_id as keyof typeof LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION
            ];

        const allUnits =
            element?.all_units && element?.all_units?.length !== 0
                ? element.all_units
                : element?.unit
                ? [element?.unit]
                : [];

        return (
            <div className={`flex justify-between items-center`}>
                <div
                    className={`flex ml-8 items-center w-176 lab-tests-cell-input-container ${
                        input ? 'filled-lab-tests-cell-input-container' : ''
                    }  ${isHighLowAbnormalInterpretation ? 'low-high-interpretation' : ''}`}
                    style={{ fontWeight: 100, maxHeight: 32 }}
                >
                    <Input
                        type={'text'}
                        disabled={disabled}
                        placeholder={'00'}
                        defaultValue={''}
                        value={input}
                        onChange={this.handleInputChange}
                        size={'large'}
                        className={`rounded-4`}
                        addonAfter={
                            <div className="relative flex items-center">
                                {isHighLowAbnormalInterpretation?.status === 'high' && (
                                    <Tooltip
                                        placement="top"
                                        title={isHighLowAbnormalInterpretation?.message}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 12 13"
                                            className="z-10 absolute -left-28 w-12 h-13"
                                        >
                                            <path
                                                d="M5.37109 0.898438C5.69922 0.542969 6.27344 0.542969 6.60156 0.898438L10.9766 5.27344C11.2227 5.51953 11.3047 5.90234 11.168 6.23047C11.0312 6.55859 10.7305 6.77734 10.375 6.77734H7.75V11.7812C7.75 12.4102 7.25781 12.875 6.65625 12.875H5.34375C4.71484 12.875 4.25 12.4102 4.25 11.7812V6.75H1.625C1.26953 6.75 0.941406 6.55859 0.804688 6.23047C0.667969 5.90234 0.75 5.51953 0.996094 5.27344L5.37109 0.898438Z"
                                                fill="#D73A2F"
                                            />
                                        </svg>
                                    </Tooltip>
                                )}
                                {isHighLowAbnormalInterpretation?.status === 'low' && (
                                    <Tooltip
                                        placement="top"
                                        title={isHighLowAbnormalInterpretation?.message}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 12 13"
                                            className="z-10 absolute -left-28 w-12 h-13"
                                        >
                                            <path
                                                d="M6.60156 12.6289C6.27344 12.9844 5.69922 12.9844 5.37109 12.6289L0.996094 8.25391C0.75 8.00781 0.667969 7.625 0.804688 7.29688C0.941406 6.96875 1.26953 6.75 1.625 6.75H4.25V1.71875C4.25 1.11719 4.71484 0.625 5.34375 0.625H6.65625C7.25781 0.625 7.75 1.11719 7.75 1.71875V6.75H10.375C10.7031 6.75 11.0312 6.96875 11.168 7.29688C11.3047 7.625 11.2227 8.00781 10.9766 8.25391L6.60156 12.6289Z"
                                                fill="#D73A2F"
                                            />
                                        </svg>
                                    </Tooltip>
                                )}
                                {isHighLowAbnormalInterpretation?.status === 'abnormal' && (
                                    <Tooltip
                                        placement="top"
                                        title={isHighLowAbnormalInterpretation?.message}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 16 13"
                                            className="z-10 absolute -left-30 w-16 h-13"
                                        >
                                            <path
                                                d="M8 0.625C8.38281 0.625 8.73828 0.84375 8.92969 1.17188L14.8359 11.2344C15.0273 11.5898 15.0273 12 14.8359 12.3281C14.6445 12.6836 14.2891 12.875 13.9062 12.875H2.09375C1.68359 12.875 1.32812 12.6836 1.13672 12.3281C0.945312 12 0.945312 11.5898 1.13672 11.2344L7.04297 1.17188C7.23438 0.84375 7.58984 0.625 8 0.625ZM8 4.125C7.61719 4.125 7.34375 4.42578 7.34375 4.78125V7.84375C7.34375 8.22656 7.61719 8.5 8 8.5C8.35547 8.5 8.65625 8.22656 8.65625 7.84375V4.78125C8.65625 4.42578 8.35547 4.125 8 4.125ZM8.875 10.25C8.875 9.78516 8.46484 9.375 8 9.375C7.50781 9.375 7.125 9.78516 7.125 10.25C7.125 10.7422 7.50781 11.125 8 11.125C8.46484 11.125 8.875 10.7422 8.875 10.25Z"
                                                fill="#D73A2F"
                                            />
                                        </svg>
                                    </Tooltip>
                                )}

                                <div>
                                    <div className="flex items-center space-x-4">
                                        <select
                                            disabled={disabled}
                                            className="bg-transparent text-14 text-darwin-neutral-800 lab-vital-unit-select"
                                            value={element?.unit?.id}
                                            onChange={(e) => {
                                                const unit = element?.all_units?.find(
                                                    (unit) => unit.id == e.target.value,
                                                );

                                                this.onClickSave(input || '', unit);
                                            }}
                                        >
                                            {allUnits?.length === 0 && (
                                                <option disabled selected value="undefined">
                                                    --
                                                </option>
                                            )}

                                            {allUnits?.map((unit) => {
                                                return (
                                                    <option value={unit?.id}>{unit?.name}</option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        }
                    />
                </div>
            </div>
        );
    }
}
